import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth } from "../App";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  Bug, Search, Loader2, RefreshCw, CheckCircle2, Trash2, X,
  AlertTriangle, AlertCircle, AlertOctagon, Info, Eye, FileText, Camera,
  Shield, ExternalLink, MessageSquare,
} from "lucide-react";
import { toast } from "sonner";
import { Link, Navigate } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const SEVERIDAD_INFO = {
  bajo: { label: "Bajo", color: "bg-emerald-100 text-emerald-700 border-emerald-300", icon: Info },
  medio: { label: "Medio", color: "bg-amber-100 text-amber-700 border-amber-300", icon: AlertCircle },
  alto: { label: "Alto", color: "bg-orange-100 text-orange-700 border-orange-300", icon: AlertTriangle },
  critico: { label: "Crítico", color: "bg-rose-100 text-rose-700 border-rose-300", icon: AlertOctagon },
};

const STATUS_INFO = {
  nuevo: { label: "Nuevo", color: "bg-blue-100 text-blue-700 border-blue-300" },
  en_revision: { label: "En Revisión", color: "bg-violet-100 text-violet-700 border-violet-300" },
  en_proceso: { label: "En Proceso", color: "bg-amber-100 text-amber-700 border-amber-300" },
  resuelto: { label: "Resuelto", color: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  cerrado: { label: "Cerrado", color: "bg-slate-200 text-slate-700 border-slate-300" },
  no_aplica: { label: "No Aplica", color: "bg-zinc-100 text-zinc-600 border-zinc-300" },
};

export default function AdminReportesErroresPage() {
  const { user } = useAuth();
  const isAdmin = useMemo(() => {
    const r = (user?.role || "").toLowerCase();
    return r === "admin" || r === "administrador" || r === "superadmin";
  }, [user]);

  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const authHeaders = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterSeveridad, setFilterSeveridad] = useState("all");
  const [filterModulo, setFilterModulo] = useState("all");
  const [opciones, setOpciones] = useState({ modulos: [], estados: [], severidades: [] });

  const [selected, setSelected] = useState(null);
  const [statusDraft, setStatusDraft] = useState("nuevo");
  const [notesDraft, setNotesDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/reportes-errores?limit=300`, authHeaders());
      setItems(r.data.items || []);
    } catch (e) {
      toast.error("No se pudieron cargar los reportes");
    }
    setLoading(false);
  }, [authHeaders]);

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/reportes-errores/_options/all`, authHeaders());
        setOpciones(r.data);
      } catch { /* noop */ }
    })();
    fetchAll();
  }, [isAdmin, fetchAll, authHeaders]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((r) => {
      if (filterStatus !== "all" && r.status !== filterStatus) return false;
      if (filterSeveridad !== "all" && r.severidad !== filterSeveridad) return false;
      if (filterModulo !== "all" && r.modulo !== filterModulo) return false;
      if (!q) return true;
      return (
        (r.asunto || "").toLowerCase().includes(q) ||
        (r.descripcion || "").toLowerCase().includes(q) ||
        (r.user_name || "").toLowerCase().includes(q) ||
        (r.user_email || "").toLowerCase().includes(q)
      );
    });
  }, [items, search, filterStatus, filterSeveridad, filterModulo]);

  const stats = useMemo(() => {
    return {
      total: items.length,
      nuevos: items.filter((r) => r.status === "nuevo").length,
      en_proceso: items.filter((r) => r.status === "en_proceso" || r.status === "en_revision").length,
      resueltos: items.filter((r) => r.status === "resuelto").length,
      criticos: items.filter((r) => r.severidad === "critico" && !["resuelto", "cerrado", "no_aplica"].includes(r.status)).length,
    };
  }, [items]);

  const openReport = (r) => {
    setSelected(r);
    setStatusDraft(r.status || "nuevo");
    setNotesDraft(r.admin_notes || "");
    setConfirmDelete(false);
  };

  const saveStatus = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await axios.patch(
        `${API}/reportes-errores/${selected.id}/status`,
        { status: statusDraft, admin_notes: notesDraft || null },
        authHeaders()
      );
      const wasResolved = statusDraft === "resuelto" && selected.status !== "resuelto";
      toast.success(
        wasResolved
          ? "Reporte resuelto. Se notificó al usuario y se publicó el aviso global."
          : "Estado actualizado correctamente."
      );
      setSelected(null);
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar el estado");
    }
    setSaving(false);
  };

  const deleteReport = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/reportes-errores/${selected.id}`, authHeaders());
      toast.success("Reporte eliminado");
      setSelected(null);
      setConfirmDelete(false);
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar el reporte");
    }
    setSaving(false);
  };

  if (!user) return null;
  if (!isAdmin) return <Navigate to="/" replace />;

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 via-white to-rose-50/30 dark:from-slate-950 dark:via-slate-900 dark:to-rose-950/20">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="admin-reportes-errores-page">

        {/* Hero */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-slate-900 via-slate-800 to-rose-900 text-white px-6 py-7 mb-7 shadow-xl">
          <div className="absolute -right-8 -top-8 w-48 h-48 rounded-full bg-rose-500/20 blur-3xl" />
          <div className="relative flex items-start gap-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 ring-1 ring-white/20">
              <Shield className="w-7 h-7" />
            </div>
            <div className="flex-1">
              <Badge className="bg-rose-500/90 text-white border-0 font-semibold tracking-wide text-[10px] uppercase mb-2">
                Soporte · Admin
              </Badge>
              <h1 className="text-2xl md:text-3xl font-bold font-['Manrope'] tracking-tight">
                Gestión de Reportes de Errores
              </h1>
              <p className="text-white/80 mt-1 text-sm max-w-2xl">
                Revisá los problemas reportados por los usuarios y marcalos como resueltos.
                Al cambiar el estado a <strong>Resuelto</strong>, se envía un correo al reportador
                y aparece un aviso popup global para toda la plataforma.
              </p>
            </div>
            <Link to="/reportar-error" className="hidden md:inline">
              <Button variant="secondary" size="sm" className="bg-white/15 hover:bg-white/25 text-white border-0">
                <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                Vista usuario
              </Button>
            </Link>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          {[
            { label: "Total", value: stats.total, color: "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200" },
            { label: "Nuevos", value: stats.nuevos, color: "bg-blue-100 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300" },
            { label: "En proceso", value: stats.en_proceso, color: "bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300" },
            { label: "Resueltos", value: stats.resueltos, color: "bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300" },
            { label: "Críticos abiertos", value: stats.criticos, color: "bg-rose-100 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300" },
          ].map((k) => (
            <Card key={k.label} className={`border-0 shadow-sm ${k.color}`} data-testid={`kpi-${k.label.toLowerCase().replace(/\s/g, "-")}`}>
              <CardContent className="py-4 px-4 text-center">
                <p className="text-[10px] uppercase tracking-wider opacity-70 font-medium">{k.label}</p>
                <p className="text-2xl font-bold mt-0.5">{k.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <Card className="border-slate-200 dark:border-slate-800 mb-5">
          <CardContent className="py-4">
            <div className="flex flex-col md:flex-row md:items-center gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar por asunto, descripción, usuario…"
                  className="pl-9"
                  data-testid="admin-search-input"
                />
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-full md:w-44" data-testid="filter-status-select"><SelectValue placeholder="Estado" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  {(opciones.estados || []).map((s) => (
                    <SelectItem key={s} value={s}>{STATUS_INFO[s]?.label || s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterSeveridad} onValueChange={setFilterSeveridad}>
                <SelectTrigger className="w-full md:w-40" data-testid="filter-severidad-select"><SelectValue placeholder="Severidad" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toda severidad</SelectItem>
                  {(opciones.severidades || []).map((s) => (
                    <SelectItem key={s} value={s}>{SEVERIDAD_INFO[s]?.label || s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterModulo} onValueChange={setFilterModulo}>
                <SelectTrigger className="w-full md:w-56" data-testid="filter-modulo-select"><SelectValue placeholder="Módulo" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los módulos</SelectItem>
                  {(opciones.modulos || []).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading} data-testid="refresh-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <Card className="border-dashed border-2 border-slate-200 dark:border-slate-800">
            <CardContent className="py-12 text-center text-slate-400">
              <Bug className="w-10 h-10 mx-auto opacity-30 mb-2" />
              <p>No hay reportes que coincidan con los filtros.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2.5" data-testid="admin-reports-list">
            {filtered.map((r) => {
              const sevInfo = SEVERIDAD_INFO[r.severidad] || SEVERIDAD_INFO.medio;
              const stInfo = STATUS_INFO[r.status] || STATUS_INFO.nuevo;
              const SevIcon = sevInfo.icon;
              return (
                <Card
                  key={r.id}
                  className="border border-slate-200 dark:border-slate-800 hover:border-rose-300 dark:hover:border-rose-700 transition-colors cursor-pointer group"
                  onClick={() => openReport(r)}
                  data-testid={`report-row-${r.id}`}
                >
                  <CardContent className="py-3 px-4">
                    <div className="flex items-start gap-3">
                      <div className={`rounded-lg p-2 ${sevInfo.color} border`}>
                        <SevIcon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-0.5">
                          <p className="font-semibold text-sm text-slate-800 dark:text-slate-100 truncate">
                            {r.asunto}
                          </p>
                          <Badge variant="outline" className={`text-[9px] px-1.5 py-0 ${stInfo.color}`}>{stInfo.label}</Badge>
                          <Badge variant="outline" className={`text-[9px] px-1.5 py-0 ${sevInfo.color}`}>{sevInfo.label}</Badge>
                          {(r.screenshot_urls || []).length > 0 && (
                            <Badge variant="outline" className="text-[9px] px-1.5 py-0 bg-slate-100 text-slate-600 border-slate-200">
                              <Camera className="w-2.5 h-2.5 mr-0.5" /> {r.screenshot_urls.length}
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-1 leading-snug">{r.descripcion}</p>
                        <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1">
                          {r.user_name || r.user_email} · {r.modulo} · {new Date(r.created_at).toLocaleString("es-DO", { dateStyle: "short", timeStyle: "short" })}
                        </p>
                      </div>
                      <Eye className="w-4 h-4 text-slate-300 group-hover:text-rose-500 transition-colors" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Detail modal */}
        <Dialog open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
          <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="admin-report-detail-dialog">
            {selected && (
              <>
                <DialogHeader>
                  <div className="flex items-start gap-2 flex-wrap">
                    <DialogTitle className="flex-1 text-base">{selected.asunto}</DialogTitle>
                    <Badge variant="outline" className={`text-[10px] ${STATUS_INFO[selected.status]?.color}`}>{STATUS_INFO[selected.status]?.label || selected.status}</Badge>
                    <Badge variant="outline" className={`text-[10px] ${SEVERIDAD_INFO[selected.severidad]?.color}`}>{SEVERIDAD_INFO[selected.severidad]?.label || selected.severidad}</Badge>
                  </div>
                  <DialogDescription className="text-[11px]">
                    Reportado por <strong>{selected.user_name}</strong> ({selected.user_email}) ·{" "}
                    {new Date(selected.created_at).toLocaleString("es-DO")} · Módulo: <strong>{selected.modulo}</strong>
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 mt-2 text-sm">
                  <section>
                    <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1 font-semibold flex items-center gap-1">
                      <FileText className="w-3 h-3" /> Descripción
                    </p>
                    <div className="rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 p-3 whitespace-pre-wrap text-[13px] text-slate-700 dark:text-slate-200">
                      {selected.descripcion}
                    </div>
                  </section>

                  {selected.pasos_para_reproducir && (
                    <section>
                      <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1 font-semibold">Pasos para reproducir</p>
                      <div className="rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 p-3 whitespace-pre-wrap font-mono text-[12px] text-slate-600 dark:text-slate-300">
                        {selected.pasos_para_reproducir}
                      </div>
                    </section>
                  )}

                  {(selected.screenshot_urls || []).length > 0 && (
                    <section>
                      <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1 font-semibold flex items-center gap-1">
                        <Camera className="w-3 h-3" /> Capturas ({selected.screenshot_urls.length})
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {selected.screenshot_urls.map((u, i) => (
                          <a key={i} href={`${process.env.REACT_APP_BACKEND_URL}${u}`} target="_blank" rel="noreferrer">
                            <img src={`${process.env.REACT_APP_BACKEND_URL}${u}`} alt="" className="w-full h-28 object-cover rounded-md border border-slate-200 hover:opacity-80 transition-opacity" />
                          </a>
                        ))}
                      </div>
                    </section>
                  )}

                  <section className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[12px]">
                    {selected.url_problema && (
                      <div>
                        <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-0.5 font-semibold">URL</p>
                        <a href={selected.url_problema} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline break-all">{selected.url_problema}</a>
                      </div>
                    )}
                    {selected.contacto_telefono && (
                      <div>
                        <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-0.5 font-semibold">Teléfono contacto</p>
                        <p className="text-slate-600">{selected.contacto_telefono}</p>
                      </div>
                    )}
                    {selected.navegador && (
                      <div className="md:col-span-2">
                        <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-0.5 font-semibold">Navegador / Dispositivo</p>
                        <p className="text-slate-500 text-[11px] break-all">{selected.navegador} {selected.dispositivo ? ` · ${selected.dispositivo}` : ""}</p>
                      </div>
                    )}
                  </section>

                  <section className="border-t pt-4 space-y-3">
                    <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-1">
                      <MessageSquare className="w-3 h-3" /> Gestión administrativa
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Estado</Label>
                        <Select value={statusDraft} onValueChange={setStatusDraft}>
                          <SelectTrigger data-testid="status-draft-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {(opciones.estados || []).map((s) => (
                              <SelectItem key={s} value={s}>{STATUS_INFO[s]?.label || s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {statusDraft === "resuelto" && selected.status !== "resuelto" && (
                          <p className="text-[10px] text-emerald-600 mt-1 flex items-start gap-1">
                            <CheckCircle2 className="w-3 h-3 mt-0.5 flex-shrink-0" />
                            Al guardar, el reportador recibirá un correo y aparecerá un popup global para todos los usuarios.
                          </p>
                        )}
                      </div>
                      <div>
                        <Label className="text-xs">Notas internas / Mensaje al reportador</Label>
                        <Textarea
                          value={notesDraft}
                          onChange={(e) => setNotesDraft(e.target.value)}
                          placeholder="Ej: Solucionado en versión 1.2.5. Reinicia el navegador."
                          rows={3}
                          className="resize-y"
                          data-testid="admin-notes-textarea"
                        />
                      </div>
                    </div>
                  </section>
                </div>

                <DialogFooter className="flex-col-reverse sm:flex-row gap-2 mt-3">
                  {confirmDelete ? (
                    <div className="flex items-center gap-2 mr-auto bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800 rounded-lg px-3 py-2">
                      <p className="text-xs text-rose-700 dark:text-rose-300">¿Eliminar definitivamente?</p>
                      <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(false)} className="h-7 px-2">
                        <X className="w-3 h-3 mr-1" /> No
                      </Button>
                      <Button size="sm" variant="destructive" onClick={deleteReport} disabled={saving} className="h-7 px-2" data-testid="confirm-delete-btn">
                        <Trash2 className="w-3 h-3 mr-1" /> Sí, eliminar
                      </Button>
                    </div>
                  ) : (
                    <Button variant="ghost" onClick={() => setConfirmDelete(true)} className="text-rose-600 hover:text-rose-700 mr-auto" data-testid="delete-btn">
                      <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Eliminar
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => setSelected(null)}>Cerrar</Button>
                  <Button
                    onClick={saveStatus}
                    disabled={saving}
                    className="bg-gradient-to-r from-rose-600 to-orange-500 hover:from-rose-700 hover:to-orange-600 text-white"
                    data-testid="save-status-btn"
                  >
                    {saving ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Guardando…</> : <><CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Guardar cambios</>}
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
